import { Component, lazy, Suspense, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Move3d, Wrench, Truck, Handshake, Gamepad2 } from "lucide-react";
import { MaskLines, EASE } from "@/components/motion/Reveal";
import { scrollToTarget } from "@/lib/lenis";
import { INTRO_DELAY as D } from "@/lib/intro";
import { Magnetic } from "@/components/fx/Motion";
import { IMG } from "@/data/site";
import { LegacyBadge } from "@/components/sections/Legacy";

const ArcadeStage = lazy(() => import("@/components/three/ArcadeCabinet"));

class WebGLBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const playClaw = () => scrollToTarget("#claw-game-box", { offset: -96, onComplete: () => document.getElementById("claw-game-box")?.focus({ preventScroll: true }) });

const PlayClawButton = () => (
  <motion.div initial={{ opacity: 0, y: -20, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 1.5 + D, type: "spring", stiffness: 180, damping: 16 }} className="absolute inset-x-0 top-0 z-20 flex justify-center">
    <Magnetic strength={0.25}>
      <button onClick={playClaw} className="group relative flex items-center gap-4 overflow-hidden rounded-full bg-gradient-to-r from-signal via-[#ff4d5f] to-signal py-2.5 pl-2.5 pr-7 text-left text-white shadow-[0_8px_0_#8a0f1d,0_0_60px_-6px_rgba(255,42,64,0.9)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_12px_0_#8a0f1d,0_0_80px_-4px_rgba(255,42,64,1)] active:translate-y-1 active:shadow-[0_3px_0_#8a0f1d]" data-testid="hero-play-claw-button">
        <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
        <span className="relative grid h-12 w-12 place-items-center rounded-full bg-white text-signal sm:h-14 sm:w-14">
          <span className="absolute inset-0 animate-ping rounded-full bg-white/50" />
          <Gamepad2 className="relative h-6 w-6" />
        </span>
        <span className="relative flex flex-col leading-none">
          <span className="font-display text-lg font-black uppercase tracking-wide sm:text-xl">Play Now</span>
          <span className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/85">Try the claw · Win a prize</span>
        </span>
      </button>
    </Magnetic>
  </motion.div>
);

const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, ease: EASE, delay: d + D } });

const Fallback = () => <img src={IMG.arcadeRed} alt="Arcade cabinet" className="h-full w-full rounded-3xl object-cover opacity-70" />;

const Facts = () => (
  <motion.ul {...fade(1.35)} className="mt-14 grid max-w-xl grid-cols-3 gap-4 border-t border-white/10 pt-6">
    {[[Handshake, "Partner or purchase"], [Truck, "Delivery & install"], [Wrench, "Service & repair"]].map(([Icon, label]) => (
      <li key={label} className="flex flex-col gap-2 text-xs text-slate-400 sm:flex-row sm:items-center sm:text-sm">
        <Icon className="h-4 w-4 text-volt" /> {label}
      </li>
    ))}
  </motion.ul>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const textO = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden pt-28 lg:pt-24" data-testid="hero-section">
      <div className="dot-grid absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_70%_40%,black,transparent_70%)]" />
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute right-[-10%] top-[5%] h-[70vh] w-[70vh] rounded-full bg-volt/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-[20%] h-[40vh] w-[40vh] rounded-full bg-signal/10 blur-[120px]" />

      <div className="container-x relative grid min-h-[calc(100svh-7rem)] items-center gap-6 lg:grid-cols-12">
        <motion.div style={{ y: textY, opacity: textO }} className="relative z-10 lg:col-span-7">
          <motion.p {...fade(0.3)} className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-volt" /> Over 65 Years · Tennessee's Amusement Partner
          </motion.p>
          <h1 className="mt-7 font-display font-black uppercase leading-[0.84] tracking-[-0.045em] text-white" data-testid="hero-title" aria-label="Brewer Amusement">
            <MaskLines
              delay={0.45 + D}
              stagger={0.14}
              lines={["Brewer", <>Amusement<span className="text-volt">.</span></>]}
              lineClass={(i) => (i === 1 ? "text-outline text-[clamp(2.6rem,8.6vw,6.4rem)]" : "text-[clamp(3.6rem,12vw,10.5rem)]")}
            />
          </h1>
          <div className="mt-8 max-w-xl">
            <h2 className="font-display text-lg font-semibold leading-snug text-white md:text-2xl">
              <MaskLines delay={0.85 + D} lines={["Entertainment That Keeps", "Customers Coming Back"]} />
            </h2>
            <motion.p {...fade(1.1)} className="mt-5 text-base leading-relaxed text-slate-400">
              Commercial amusement equipment, installation, service, and support for businesses.
            </motion.p>
          </div>
          <motion.div {...fade(1.2)} className="mt-10 flex flex-wrap gap-4">
            <Magnetic>
              <button onClick={() => scrollToTarget("#equipment")} className="btn-primary h-14 px-8 text-base" data-testid="hero-view-equipment-button">
                View Equipment <ArrowRight className="btn-arrow h-4 w-4" />
              </button>
            </Magnetic>
            <Magnetic>
              <button onClick={() => scrollToTarget("#get-started")} className="btn-ghost h-14 px-8 text-base" data-testid="hero-get-started-button">
                Get Started
              </button>
            </Magnetic>
          </motion.div>
          <Facts />
        </motion.div>
        <motion.button {...fade(1.6)} onClick={() => scrollToTarget("#equipment")} className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 lg:flex" data-testid="hero-scroll-cue">
          Scroll
          <span className="relative h-10 w-px overflow-hidden bg-white/10"><motion.span className="absolute left-0 top-0 h-4 w-px bg-volt" animate={{ y: [-16, 40] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} /></span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.6 + D }}
          className="relative h-[560px] sm:h-[660px] lg:col-span-5 lg:h-[780px]"
        >
          <PlayClawButton />
          <div className="absolute inset-x-[10%] bottom-[6%] h-10 rounded-[50%] bg-volt/20 blur-2xl" />
          <div className="absolute inset-x-0 bottom-0 top-[110px] sm:top-[130px]">
            <WebGLBoundary fallback={<Fallback />}>
              <Suspense fallback={null}>
                <ArcadeStage />
              </Suspense>
            </WebGLBoundary>
          </div>
          <div className="pointer-events-none absolute left-0 top-[150px] hidden rounded-2xl border border-white/10 bg-ink-900/70 px-4 py-3 backdrop-blur-xl sm:block">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Model</p>
            <p className="mt-1 font-display text-sm font-semibold text-white">BA-01 Upright Cabinet</p>
          </div>
          <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 1.4 + D, type: "spring", stiffness: 160, damping: 14 }} className="absolute bottom-16 left-0 z-10 sm:bottom-20">
            <LegacyBadge />
          </motion.div>
          <div className="pointer-events-none absolute bottom-6 right-0 hidden items-center gap-2 rounded-full border border-white/10 bg-ink-900/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300 backdrop-blur-xl sm:flex" data-testid="hero-drag-hint">
            <Move3d className="h-3.5 w-3.5 text-volt" /> Drag to rotate
          </div>
        </motion.div>
      </div>
    </section>
  );
};
